import React, { useState, useEffect } from "react";
import { useAuth } from "context/AuthContext";
import { Button } from "./ui/Button";
import { Modal } from "./ui/Modal";
import { TextEdit } from "./ui/TextEdit";
import { Teacher } from "types/Teacher";
import fetchWithCredentials from "apiClient";
import { Search, User, Mail, MapPin, School } from "lucide-react";

interface ImpersonationDialogProps {
  open: boolean;
  onClose: () => void;
}

export function ImpersonationDialog({
  open,
  onClose,
}: ImpersonationDialogProps) {
  const { startImpersonation } = useAuth();
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [filteredTeachers, setFilteredTeachers] = useState<Teacher[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      fetchTeachers();
    }
  }, [open]);

  useEffect(() => {
    const filtered = teachers.filter(
      (teacher) =>
        teacher.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        teacher.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        teacher.country.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (teacher.school &&
          teacher.school.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredTeachers(filtered);
  }, [teachers, searchTerm]);

  const fetchTeachers = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchWithCredentials("/api/teachers/");
      setTeachers(data);
    } catch (err: any) {
      setError(err.message || "Failed to fetch teachers");
    } finally {
      setLoading(false);
    }
  };

  const handleImpersonate = async (teacherUserId: number) => {
    try {
      await startImpersonation(teacherUserId);
      onClose();
    } catch (err: any) {
      setError(err.message || "Failed to start impersonation");
    }
  };

  return (
    <Modal isOpen={open} onClose={onClose} title="Impersonate Teacher">
      <div className="max-w-2xl w-full">
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 z-10" />
            <TextEdit
              id="teacher-search"
              name="teacher-search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              label=""
              placeholder="Search teachers by name, email, country, or school..."
              className="pl-10"
            />
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto space-y-2">
            {filteredTeachers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {searchTerm
                  ? "No teachers found matching your search."
                  : "No teachers available."}
              </div>
            ) : (
              filteredTeachers.map((teacher) => (
                <div
                  key={teacher.userId}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <User className="w-4 h-4 text-gray-400" />
                        <span className="font-medium text-gray-900">
                          {teacher.name}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2 mb-1">
                        <Mail className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          {teacher.email}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2 mb-1">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          {teacher.country}
                        </span>
                      </div>
                      {teacher.school && (
                        <div className="flex items-center space-x-2">
                          <School className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {teacher.school}
                          </span>
                        </div>
                      )}
                    </div>
                    <Button
                      onClick={() => handleImpersonate(teacher.userId)}
                      size="sm"
                      className="ml-4"
                    >
                      Impersonate
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
